import {
  ExtranetCustomerUserProfessionalRoleDto,
  ExtranetLanguageOption,
  ExtranetProductProfessionalRoleDto,
  ExtranetShopProductDto,
} from '@pharmac-extranet/extranet-api'
import { useLanguage } from '@pharmac-extranet/shared'
import { PharmacSuccessToast, PharmacText, usePharmacToast } from '@pharmac/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { useUserCustomerDetails } from '../../features/customer/useUserCustomerDetails'
import { api } from '../../services/HttpService'

export const useShop = (productId?: number, product?: ExtranetShopProductDto) => {
  const { t } = useLanguage()
  const { userCustomerRoles } = useUserCustomerDetails()
  const { customer } = useCustomerContext()
  const toastHook = usePharmacToast()
  const queryClient = useQueryClient()
  const { user } = useAuthenticationContext()

  const { mutate: subscribeToFreeProduct } = useMutation({
    mutationFn: () =>
      api.shop.subscribeToFreeProduct(Number(customer?.id), {
        productId: Number(productId),
      }),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: <PharmacSuccessToast>{t('extranet:shop.addedToOwnProducts')}</PharmacSuccessToast>,
      })
      await queryClient.invalidateQueries({
        queryKey: ['__shopProducts', customer?.id],
      })
      await queryClient.invalidateQueries({
        queryKey: ['getAvailableProductCategories'],
      })
    },
  })

  const { mutate: deleteSubscription } = useMutation({
    mutationFn: () =>
      api.shop.deleteFreeProductSubscription(Number(customer?.id), Number(productId)),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>{t('extranet:shop.removedFromOwnProducts')}</PharmacSuccessToast>
        ),
      })
      await queryClient.invalidateQueries({
        queryKey: ['__shopProducts', customer?.id],
      })
      await queryClient.invalidateQueries({
        queryKey: ['getAvailableProductCategories'],
      })
    },
  })

  const hasBuyerRole = !!userCustomerRoles?.roles.find((a) => a.role === 'customer-buyer')
  const hasBuyingBan = customer?.buyingBan

  const accessPeriodDuration = (durationUnit?: string) => {
    if (durationUnit === 'day')
      return <PharmacText paddingSmallLeft>{t('common:dayShort')}</PharmacText>
    if (durationUnit === 'month')
      return <PharmacText paddingSmallLeft>{t('common:monthShort')}</PharmacText>
    if (durationUnit === 'year')
      return <PharmacText paddingSmallLeft>{t('common:yearShort')}</PharmacText>
    return
  }

  const formatTimecode = (timecode: number) => {
    return new Date(timecode * 1000).toISOString().slice(11, 19)
  }

  const productCategory = (productFamily: string) => {
    if (productFamily === 'recording-product') return t('extranet:shopProducts.video')
    if (productFamily === 'document-product') return t('extranet:shopProducts.document')
    if (productFamily === 'service-product') return t('extranet:shopProducts.serviceProduct')
    if (productFamily === 'physical-product') return t('extranet:shopProducts.physicalProduct')
  }

  const langLabel = (lang: string) => {
    if (lang === 'fi') return t('common:fi')
    if (lang === 'sv') return t('common:sv')
    if (lang === 'en') return t('common:en')
  }

  const langQuery = (lang: string) => {
    if (lang === 'fi') return ExtranetLanguageOption.Fi
    if (lang === 'sv') return ExtranetLanguageOption.Sv
    if (lang === 'en') return ExtranetLanguageOption.En
  }

  const { data: myRoles } = useQuery({
    queryKey: ['__myCustomerUserRoles', { userId: user?.id, customerId: customer?.id }],
    queryFn: async () => await api.customers.getMyCustomerUserRoles(customer?.id as number),
    enabled: !!user?.id && !!customer?.id,
  })

  const formatTimecodeToFancyForm = (timecode: number) => {
    const dateTime = new Date(timecode * 1000)
    const hours = dateTime.getUTCHours()
    const minutes = dateTime.getUTCMinutes()
    const seconds = dateTime.getUTCSeconds()
    const roundedMinutes = hours && seconds > 30 ? minutes + 1 : minutes

    return `${hours !== 0 ? hours + ' h ' : ''}${roundedMinutes !== 0 ? roundedMinutes + ' min ' : ''}${seconds !== 0 && hours === 0 ? seconds + ' s' : ''}`
  }

  const hasRequiredRoles = (
    productRoles?: ExtranetProductProfessionalRoleDto[],
    userRoles?: ExtranetCustomerUserProfessionalRoleDto[]
  ) => {
    if (product?.professionalRoles && product?.professionalRoles.length > 0)
      return productRoles?.some((pr) =>
        userRoles?.find((ur) => pr.displayName === ur.displayName.fi)
      )
    return true
  }

  return {
    hasBuyerRole,
    hasBuyingBan,
    subscribeToFreeProduct,
    accessPeriodDuration,
    formatTimecode,
    productCategory,
    deleteSubscription,
    langLabel,
    langQuery,
    myRoles,
    formatTimecodeToFancyForm,
    hasRequiredRoles,
  }
}
